import React, { useState } from "react";
import { Card, Button, Select, Dropdown, Menu, Space, message, Modal, Col, Image, Input, Typography } from "antd";
import {
  DownloadOutlined,
  FilePdfOutlined,
  PictureOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  FileTextOutlined,
  FileOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import DocumentForm from "./DocumentForm";
import { useAuth } from "../../layout/auth";

const DocumentCard = ({ doc, fetchDocuments, verification, user }) => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(doc.Tipo);
  const [statusValue, setStatusValue] = useState(doc.Status);
  const [imageError, setImageError] = useState(false);
  const auth = useAuth();

  const rutaAcceso = doc?.RutaAcceso || "";
  const isPdf = rutaAcceso.toLowerCase().endsWith(".pdf");
  const isImage = rutaAcceso.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
  const isDoc = rutaAcceso.toLowerCase().match(/\.(doc|docx)$/);
  const isExcel = rutaAcceso.toLowerCase().match(/\.(xls|xlsx)$/);

  const getFileExtension = (filename) => {
    if (!filename) return "FILE";
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
  };

  const renderPreview = () => {
    if (!rutaAcceso) {
      return (
        <div
          style={{
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            border: "2px dashed #d9d9d9",
          }}
        >
          <div style={{ textAlign: "center", color: "#999" }}>
            <FileOutlined style={{ fontSize: "32px", marginBottom: "8px" }} />
            <div style={{ fontSize: "14px" }}>Sin archivo</div>
          </div>
        </div>
      );
    }

    if (isImage && !imageError) {
      return (
        <div
          style={{
            height: "200px",
            overflow: "hidden",
            borderRadius: "4px",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Image
            src={`${process.env.REACT_APP_API_URL}/documentos/${doc.DocumentoID}/download`}
            alt="Document preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            preview={{
              mask: <div style={{ color: "white", fontSize: "14px" }}>🔍 Ver imagen</div>,
            }}
            onError={() => setImageError(true)}
          />
        </div>
      );
    }

    // For non-image files, show icon with extension
    const getFileIcon = () => {
      if (isPdf) return <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: "48px" }} />;
      if (isDoc) return <FileTextOutlined style={{ color: "#1890ff", fontSize: "48px" }} />;
      if (isExcel) return <FileTextOutlined style={{ color: "#52c41a", fontSize: "48px" }} />;
      return <FileOutlined style={{ color: "#8c8c8c", fontSize: "48px" }} />;
    };

    return (
      <div
        style={{
          height: "200px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fafafa",
          borderRadius: "4px",
          border: "1px solid #f0f0f0",
          gap: "8px",
        }}
      >
        {getFileIcon()}
        <Typography.Text
          style={{
            fontSize: "14px",
            color: "#666",
            fontWeight: "600",
          }}
        >
          {getFileExtension(rutaAcceso)}
        </Typography.Text>
      </div>
    );
  };

  const handleEdit = () => {
    setIsEditModalVisible(true);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "¿Estás seguro de que deseas eliminar este documento?",
      onOk: () => {
        axios
          .delete(`${process.env.REACT_APP_API_URL}/documentos/${doc.DocumentoID}`)
          .then((res) => {
            if (res.data.success) {
              message.success("Documento eliminado exitosamente.");
              fetchDocuments();
            } else {
              message.error("Error al eliminar el documento.");
            }
          })
          .catch(() => {
            message.error("Error al eliminar el documento.");
          });
      },
    });
  };

  const handleStatusChange = (value) => {
    const statusText = value === 1 ? "Pendiente" : value === 2 ? "Validado" : "No validado";
    const newMessage = `El documento '${doc.Tipo}' de la verificación ${verification.ControlInterno} ha cambiado de status a ${statusText}`;

    let latestMessage = newMessage;

    const NotificationModal = () => {
      const [localMessage, setLocalMessage] = useState(newMessage);
      latestMessage = localMessage;

      return (
        <Space direction="vertical" style={{ width: "100%" }}>
          <span>Usuario: {user.NombreCompleto}</span>
          <span>Verificación: {verification.ControlInterno}</span>
          <span>Documento: {doc.Tipo}</span>
          <span>Status actual: {statusText}</span>
          <Input.TextArea
            placeholder="Tu mensaje"
            autoSize={{ minRows: 6, maxRows: 10 }}
            value={localMessage}
            onChange={(e) => setLocalMessage(e.target.value)}
          />
        </Space>
      );
    };

    Modal.confirm({
      title: "Cambio de status del documento",
      content: <NotificationModal />,
      onOk: () => {
        return axios
          .put(`${process.env.REACT_APP_API_URL}/documentos/${doc.DocumentoID}`, {
            Status: value,
          })
          .then((res) => {
            if (!res.data.success) throw new Error("Error al actualizar el status");

            setStatusValue(value);
            message.success("Status cambiado exitosamente a " + statusText);

            return axios.post(`${process.env.REACT_APP_API_URL}/notificaciones`, {
              UsuarioID: verification.UsuarioID,
              Mensaje: latestMessage,
              Estado: 0,
              TargetURL: `/verificaciones/${verification.VerificacionID}`,
            });
          })
          .then(() => {
            message.success("Notificación enviada al creador");
            fetchDocuments();
          })
          .catch((err) => {
            console.error("Error al actualizar o notificar:", err);
            message.error("Error al enviar notificación.");
          });
      },
      onCancel: () => {
        message.error("Cambio de status cancelado.");
      },
      okText: "Cambiar status",
      width: 800,
    });
  };

  return (
    <Col key={doc.DocumentoID} xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}>
      <Card
        size="small"
        bodyStyle={{ height: "200px", overflow: "hidden" }}
        bordered={false}
        hoverable
        extra={
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="1" onClick={handleDelete} disabled={auth.Rol !== "director"}>
                  <Space>
                    <DeleteOutlined />
                    <span>Borrar</span>
                  </Space>
                </Menu.Item>
              </Menu>
            }
          >
            <EllipsisOutlined />
          </Dropdown>
        }
        title={doc?.Tipo || "Desconocido"}
        actions={[
          <Button
            icon={<DownloadOutlined />}
            onClick={() => window.open(`${process.env.REACT_APP_API_URL}/documentos/${doc.DocumentoID}/download`, "_blank")}
            type="primary"
          >
            Descargar
          </Button>,
          <Select
            value={statusValue}
            style={{ width: 120 }}
            status={statusValue === 1 ? "warning" : statusValue === 2 ? "success" : "error"}
            onChange={handleStatusChange}
          >
            <Select.Option value={1}>Pendiente</Select.Option>
            <Select.Option value={2}>Validado</Select.Option>
            <Select.Option value={3}>No validado</Select.Option>
          </Select>,
        ]}
      >
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              padding: "8px 12px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "4px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            <div>
              Subido: <span style={{ fontWeight: "600", color: "#333" }}>{moment(doc?.CreatedAt).format("DD/MM/YYYY")}</span>
            </div>
            {verification?.FechaPapeleria && (
              <div>
                Revisado:{" "}
                <span style={{ fontWeight: "600", color: "#333" }}>{moment(verification.FechaPapeleria).format("DD/MM/YYYY")}</span>
              </div>
            )}
          </div>

          {/* File Preview */}
          {renderPreview()}
        </div>
      </Card>

      <DocumentForm
        visible={isEditModalVisible}
        setVisible={setIsEditModalVisible}
        id={doc.VerificacionID}
        selectedDocType={selectedDocType}
        setSelectedDocType={setSelectedDocType}
        fetchDocuments={fetchDocuments}
        isEditMode={true}
        documentId={doc.DocumentoID}
      />
    </Col>
  );
};

export default DocumentCard;
